import React, { useState, useEffect } from 'react';
import { MessageSquare, Calendar, Columns as LayoutColumns, Loader2, AlertCircle, Instagram, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ChatInterface } from '../components/ChatInterface';

interface Conversation {
  id: number;
  estado: 'inactive' | 'nuevo' | 'retarget' | 'low_p' | 'mid_p' | 'high_p' | 'buy_ready' | 'sold';
  canal: 'Instagram' | 'WhatsApp';
  nombre_cliente_conversacion: string;
  last_msg: string;
  last_msg_time: string;
  product_category: string | null;
  account_profile_pic_url: string | null;
  bg_color: string;
  text_color: string;
}

interface Column {
  id: Conversation['estado'];
  title: string;
}

const columns: Column[] = [
  { id: 'nuevo', title: 'Nuevo' },
  { id: 'low_p', title: 'Baja probabilidad de compra' },
  { id: 'mid_p', title: 'Probabilidad media' },
  { id: 'high_p', title: 'Alta probabilidad' },
  { id: 'buy_ready', title: 'Venta pendiente' },
  { id: 'sold', title: 'Venta realizada' },
  { id: 'inactive', title: 'Desestimado' },
];

const ConversationCard: React.FC<{ 
  conversation: Conversation;
  onClick: () => void;
}> = ({ conversation, onClick }) => {
  const getInitial = (name: string) => name.charAt(0).toUpperCase();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    }
  };

  return (
    <button
      onClick={onClick}
      className="block w-full text-left bg-white rounded-lg shadow-sm p-4"
    >
      <div className="flex items-start gap-3 max-w-full">
        <div className="relative flex-shrink-0">
          {conversation.account_profile_pic_url ? (
            <img
              src={conversation.account_profile_pic_url}
              alt={conversation.nombre_cliente_conversacion}
              className="w-12 h-12 rounded-full object-cover bg-gray-100"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium overflow-hidden"
              style={{
                backgroundColor: conversation.bg_color,
                color: conversation.text_color,
              }}
            >
              {getInitial(conversation.nombre_cliente_conversacion)}
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full shadow-sm flex items-center justify-center">
            {conversation.canal === 'Instagram' ? (
              <Instagram className="w-4 h-4 text-pink-600" />
            ) : (
              <MessageCircle className="w-4 h-4 text-green-500" />
            )}
          </div>
        </div>
        
        <div className="flex-1 min-w-0 w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-medium text-gray-900 truncate">
              {conversation.nombre_cliente_conversacion}
            </h3>
            <span className="flex-shrink-0 text-sm text-gray-500">
              {formatDate(conversation.last_msg_time)}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 line-clamp-2 mb-2 break-words">
            {conversation.last_msg}
          </p>
          
          {conversation.product_category && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 truncate max-w-full">
              {conversation.product_category}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export const Messages = () => {
  const [view, setView] = useState<'columns' | 'timeline'>('columns');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const columnsPerPage = 4;
  const totalPages = Math.ceil(columns.length / columnsPerPage);
  
  const handleNextPage = () => {
    setCurrentPage(current => Math.min(current + 1, totalPages - 1));
  };
  
  const handlePrevPage = () => {
    setCurrentPage(current => Math.max(current - 1, 0));
  };

  const fetchConversations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      console.log('Current user:', user.id);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        throw profileError;
      }
      
      if (!profile.company_id) {
        console.error('No company_id found in profile');
        throw new Error('No company associated with user');
      }

      console.log('Company ID:', profile.company_id);

      const { data, error } = await supabase
        .schema(profile.company_id)
        .from('conversaciones')
        .select('*')
        .order('last_msg_time', { ascending: false });

      if (error) {
        console.error('Conversations fetch error:', error);
        throw error;
      }

      console.log('Raw conversations data:', data);

      // Map the conversations using id_conversacion instead of id
      const validConversations = (data || []).map(conv => ({
        id: conv.id, // Use id_conversacion as the id
        estado: conv.estado,
        canal: conv.canal,
        nombre_cliente_conversacion: conv.nombre_cliente_conversacion,
        last_msg: conv.last_msg,
        last_msg_time: conv.last_msg_time,
        product_category: conv.product_category,
        account_profile_pic_url: conv.account_profile_pic_url,
        bg_color: conv.bg_color || '#e5e7eb', // Default gray if null
        text_color: conv.text_color || '#374151', // Default text color if null
      })) as Conversation[];

      console.log('Final processed conversations:', validConversations);
      setConversations(validConversations);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err.message : 'Error loading conversations');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();

    // Set up auto-refresh interval
    const intervalId = setInterval(fetchConversations, 5000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[calc(100vw-256px)]">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-gray-700" />
            <h1 className="text-2xl font-semibold text-gray-900">Mensajes</h1>
          </div>
          
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('columns')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                view === 'columns'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LayoutColumns className="w-4 h-4" />
              <span className="text-sm font-medium">Columnas</span>
            </button>
            <button
              onClick={() => setView('timeline')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                view === 'timeline'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Línea de tiempo</span>
            </button>
          </div>
        </div>
      </div>

      <div className="relative">
        {view === 'columns' ? (
          <div className="relative">
            <div className="flex items-center">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="absolute -left-4 z-10 p-2 bg-white rounded-full shadow-lg text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ top: 'calc(50% - 20px)' }}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="overflow-hidden w-full">
                <div 
                  className="flex gap-4 transition-transform duration-300 ease-in-out"
                  style={{ 
                    transform: `translateX(-${currentPage * (100 / (columns.length / columnsPerPage))}%)`,
                    width: `${(100 * columns.length) / columnsPerPage}%`
                  }}
                >
                  {columns.map(column => {
              const columnConversations = conversations.filter(
                conv => conv.estado === column.id
              );

              return (
                <div
                  key={column.id}
                  className="bg-gray-50 rounded-lg p-4 flex-1"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-medium text-gray-900">{column.title}</h2>
                    <span className="text-sm text-gray-500">
                      {columnConversations.length}
                    </span>
                  </div>
                  
                  <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-16rem)]">
                    {columnConversations.map(conversation => (
                      <ConversationCard
                        key={conversation.id}
                        conversation={conversation}
                        onClick={() => setSelectedConversation(conversation)}
                      />
                    ))}
                  </div>
                </div>
                  );
                })}
                </div>
              </div>
              
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
                className="absolute -right-4 z-10 p-2 bg-white rounded-full shadow-lg text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ top: 'calc(50% - 20px)' }}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-2xl mx-auto">
            {conversations.map(conversation => (
              <ConversationCard
                key={conversation.id}
                conversation={conversation}
                onClick={() => setSelectedConversation(conversation)}
              />
            ))}
          </div>
        )}

        {/* Chat Interface */}
        {selectedConversation && (
          <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh]">
              <ChatInterface
                conversation={selectedConversation}
                onClose={() => setSelectedConversation(null)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};